import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getTodayDashboard } from "../../lib/crm-api";
import RefreshModal from "./RefreshModal";
import type { Action, TodayPayload } from "../../lib/crm-types";
import {
  toggleEstrella,
  markActionDone,
  exitLead,
  updateActionWhat,
  logIndividualMessage,
  restoreAction,
  restoreLeadFromExit,
} from "../../lib/crm-mutations";

type Treatment = "done" | "mail" | "whatsapp" | "exited";

type TreatedAction = {
  action: Action;
  treatment: Treatment;
  origin: "late" | "due_today";
  at: Date;
};

export default function CrmToday() {
  const [data, setData] = useState<TodayPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [treated, setTreated] = useState<TreatedAction[]>([]);
  const [refreshOpen, setRefreshOpen] = useState(false);

  useEffect(() => {
    getTodayDashboard()
      .then(setData)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <main className="px-10 py-8 max-w-[1200px] mx-auto font-mono text-sm text-concrete">
        Chargement…
      </main>
    );
  }

  if (!data) {
    return (
      <main className="px-10 py-8 max-w-[1200px] mx-auto font-mono text-sm text-concrete">
        Erreur de chargement.
      </main>
    );
  }

  const findOrigin = (id: string): "late" | "due_today" => {
    if (!data) return "due_today";
    if (data.actions.late.find((a) => a.id === id)) return "late";
    return "due_today";
  };

  const moveToTreated = (id: string, treatment: Treatment) => {
    if (!data) return;
    const fromLate = data.actions.late.find((a) => a.id === id);
    const fromDue = data.actions.due_today.find((a) => a.id === id);
    const action = fromLate ?? fromDue;
    if (!action) return;
    const origin = fromLate ? "late" : "due_today";
    setTreated((t) => [{ action, treatment, origin, at: new Date() }, ...t]);
    setData((d) =>
      d
        ? {
            ...d,
            actions: {
              late: d.actions.late.filter((a) => a.id !== id),
              due_today: d.actions.due_today.filter((a) => a.id !== id),
            },
          }
        : d,
    );
  };

  const restoreFromTreated = async (t: TreatedAction) => {
    setTreated((arr) => arr.filter((x) => x.action.id !== t.action.id));
    setData((d) => {
      if (!d) return d;
      const targetList = t.origin === "late" ? "late" : "due_today";
      return {
        ...d,
        actions: {
          ...d.actions,
          [targetList]: [t.action, ...d.actions[targetList]],
        },
      };
    });
    try {
      if (t.treatment === "exited") {
        await restoreLeadFromExit(t.action.lead_id);
      } else {
        await restoreAction(t.action.id);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const updateActionLocally = (id: string, patch: Partial<Action>) => {
    setData((d) =>
      d
        ? {
            ...d,
            actions: {
              late: d.actions.late.map((a) =>
                a.id === id ? { ...a, ...patch } : a,
              ),
              due_today: d.actions.due_today.map((a) =>
                a.id === id ? { ...a, ...patch } : a,
              ),
            },
          }
        : d,
    );
  };

  const handleDone = async (action: Action) => {
    moveToTreated(action.id, "done");
    try {
      await markActionDone(action.id);
    } catch (e) {
      console.error(e);
    }
  };

  const handleToggleStar = async (action: Action) => {
    const next = !action.is_estrella;
    updateActionLocally(action.id, { is_estrella: next });
    try {
      await toggleEstrella(action.lead_id, next);
    } catch (e) {
      console.error(e);
      updateActionLocally(action.id, { is_estrella: action.is_estrella });
    }
  };

  const handleExit = async (action: Action) => {
    const ok = confirm(
      `Sortir ${action.full_name} du CRM (status=exited) ?\n\nLe lead n'apparaîtra plus dans les listes actives. Tu peux le récupérer depuis la section "Déjà traité" en bas tant que tu n'as pas rechargé la page.`,
    );
    if (!ok) return;
    moveToTreated(action.id, "exited");
    try {
      await exitLead(action.lead_id, "exit manuel depuis Today");
    } catch (e) {
      console.error(e);
    }
  };

  const handleMail = async (action: Action) => {
    if (action.primary_email) {
      const subject = encodeURIComponent(`[CRM] ${action.full_name}`);
      window.open(`mailto:${action.primary_email}?subject=${subject}`, "_blank");
    }
    moveToTreated(action.id, "mail");
    try {
      await logIndividualMessage({
        lead_id: action.lead_id,
        channel: "email",
        source_account: "manual",
        subject: `Manual mail action — ${action.what}`,
      });
      await markActionDone(action.id);
    } catch (e) {
      console.error(e);
    }
  };

  const handleWhatsapp = async (action: Action) => {
    if (action.primary_phone) {
      const num = action.primary_phone.replace(/\D/g, "");
      window.open(`https://wa.me/${num}`, "_blank");
    }
    moveToTreated(action.id, "whatsapp");
    try {
      await logIndividualMessage({
        lead_id: action.lead_id,
        channel: "whatsapp",
        source_account: "whatsapp",
        subject: `Manual WA action — ${action.what}`,
      });
      await markActionDone(action.id);
    } catch (e) {
      console.error(e);
    }
  };

  const handleEditWhat = async (action: Action, what: string) => {
    if (what === action.what) return;
    updateActionLocally(action.id, { what });
    try {
      await updateActionWhat(action.id, what);
    } catch (e) {
      console.error(e);
    }
  };
  // Suppress unused warning — kept for symmetry, may be reused elsewhere
  void findOrigin;

  const totalMails21d = data.histograms.mails_21d.reduce(
    (s, d) => s + d.mails_sent_today,
    0,
  );

  return (
    <main className="px-10 py-8 max-w-[1200px] mx-auto">
      <div className="mb-6 flex items-baseline justify-between">
        <h2 className="font-sans text-4xl font-bold tracking-tight text-charcoal">
          Aujourd'hui
        </h2>
        <button
          onClick={() => setRefreshOpen(true)}
          className="px-3 py-1.5 bg-charcoal text-ivory rounded text-xs font-mono uppercase hover:bg-black"
          title="Lancer un refresh depuis Claude Code local (Gmail + WhatsApp)"
        >
          🔄 Rafraîchir
        </button>
      </div>
      <RefreshModal
        open={refreshOpen}
        onClose={() => setRefreshOpen(false)}
        onApplied={() => {
          // Refetch dashboard after refresh
          getTodayDashboard().then(setData);
        }}
      />

      <section className="grid grid-cols-2 gap-4 mb-6">
        <ScoreCard
          label="Estrellas"
          score={data.scores.estrella.estrella_score}
          total={data.scores.estrella.estrella_total}
          overdue={data.scores.estrella.estrella_overdue}
          sublabel="en retard"
          histogram={data.histograms.scores_14d.map((d) => ({
            label: d.digest_date,
            value: d.estrella_score,
          }))}
          accent
        />
        <ScoreCard
          label="Non-Estrellas"
          score={data.scores.non_estrella.non_estrella_score}
          total={data.scores.non_estrella.non_estrella_total}
          overdue={
            data.scores.non_estrella.non_estrella_total -
            data.scores.non_estrella.recently_touched
          }
          sublabel="à relancer"
          histogram={data.histograms.scores_14d.map((d) => ({
            label: d.digest_date,
            value: d.non_estrella_score,
          }))}
        />
      </section>

      {data.actions.late.length > 0 && (
        <section className="mb-6">
          <h3 className="font-sans text-lg font-bold mb-3 text-charcoal flex items-baseline gap-3">
            <span>⚠ À rattraper en priorité</span>
            <span className="font-mono text-xs text-concrete font-normal">
              {data.actions.late.length} action
              {data.actions.late.length > 1 ? "s" : ""}
            </span>
          </h3>
          <ul className="flex flex-col gap-2">
            {data.actions.late.map((a) => (
              <ActionRow
                key={a.id}
                action={a}
                onDone={() => handleDone(a)}
                onToggleStar={() => handleToggleStar(a)}
                onExit={() => handleExit(a)}
                onMail={() => handleMail(a)}
                onWhatsapp={() => handleWhatsapp(a)}
                onEditWhat={(text) => handleEditWhat(a, text)}
              />
            ))}
          </ul>
        </section>
      )}

      {data.actions.due_today.length > 0 && (
        <section className="mb-6">
          <h3 className="font-sans text-lg font-bold mb-3 text-charcoal flex items-baseline gap-3">
            <span>Aujourd'hui</span>
            <span className="font-mono text-xs text-concrete font-normal">
              {data.actions.due_today.length} action
              {data.actions.due_today.length > 1 ? "s" : ""}
            </span>
          </h3>
          <ul className="flex flex-col gap-2">
            {data.actions.due_today.map((a) => (
              <ActionRow
                key={a.id}
                action={a}
                onDone={() => handleDone(a)}
                onToggleStar={() => handleToggleStar(a)}
                onExit={() => handleExit(a)}
                onMail={() => handleMail(a)}
                onWhatsapp={() => handleWhatsapp(a)}
                onEditWhat={(text) => handleEditWhat(a, text)}
              />
            ))}
          </ul>
        </section>
      )}

      {data.actions.late.length === 0 && data.actions.due_today.length === 0 && (
        <section className="mb-6 p-8 bg-white border border-stone rounded-lg text-center">
          <p className="font-sans text-2xl text-charcoal mb-1">
            🎉 Tout est traité
          </p>
          <p className="font-mono text-xs text-concrete">
            Plus rien à rattraper pour aujourd'hui. Profite ou prépare le prochain
            batch de touchpoints.
          </p>
        </section>
      )}

      <section className="mb-12">
        <h3 className="font-sans text-lg font-bold mb-3 text-charcoal flex items-baseline gap-3">
          <span>Mails envoyés — 21 derniers jours</span>
          <span className="font-mono text-xs text-concrete font-normal">
            total : {totalMails21d}
          </span>
        </h3>
        <div className="bg-white border border-stone rounded-lg p-6">
          <Histogram
            data={data.histograms.mails_21d.map((d) => ({
              label: d.digest_date,
              value: d.mails_sent_today,
            }))}
            height={120}
          />
        </div>
      </section>

      {treated.length > 0 && (
        <section className="mb-6">
          <h3 className="font-sans text-lg font-bold mb-3 text-concrete flex items-baseline gap-3">
            <span>Déjà traité aujourd'hui</span>
            <span className="font-mono text-xs text-ash font-normal">
              {treated.length} action{treated.length > 1 ? "s" : ""} · ↻ pour
              restaurer
            </span>
          </h3>
          <ul className="flex flex-col gap-1">
            {treated.map((t) => (
              <TreatedRow
                key={t.action.id + t.at.toISOString()}
                treated={t}
                onRestore={() => restoreFromTreated(t)}
              />
            ))}
          </ul>
        </section>
      )}

      <footer className="text-center font-mono text-[10px] uppercase text-concrete opacity-50 py-4 border-t border-stone">
        llyll CRM · v0.3 · {data.source === "mock" ? "mock data" : "supabase live"}
      </footer>
    </main>
  );
}

// ----------------------------------------------------------------------------
// Treated row (déjà traité aujourd'hui, can be restored)
// ----------------------------------------------------------------------------

function TreatedRow({
  treated,
  onRestore,
}: {
  treated: TreatedAction;
  onRestore: () => void;
}) {
  const { action, treatment, at } = treated;
  const labelMap: Record<Treatment, string> = {
    done: "✓ Marquée fait",
    mail: "✉ Mail envoyé",
    whatsapp: "💬 WhatsApp envoyé",
    exited: "✕ Sorti du CRM",
  };
  return (
    <li className="bg-stone/40 border border-stone rounded-md px-3 py-2 grid grid-cols-[1fr_auto_auto] gap-3 items-center text-sm opacity-70">
      <div className="flex items-baseline gap-2 min-w-0">
        <span className="text-charcoal line-through truncate">
          {action.full_name}
        </span>
        <span
          className="font-mono text-[9px] uppercase px-1 py-0.5 rounded text-white shrink-0"
          style={{ backgroundColor: action.category_color }}
        >
          {action.category_name}
        </span>
        <span className="text-[12px] text-concrete italic truncate">
          {action.what}
        </span>
      </div>
      <span className="font-mono text-[10px] uppercase text-concrete shrink-0">
        {labelMap[treatment]} ·{" "}
        {at.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
      </span>
      <button
        onClick={onRestore}
        title="Restaurer (annuler le traitement)"
        className="px-2 py-1 rounded border border-stone bg-white text-charcoal text-xs hover:bg-ivory hover:border-charcoal"
      >
        ↻
      </button>
    </li>
  );
}

// ----------------------------------------------------------------------------
// Components
// ----------------------------------------------------------------------------

type ActionRowProps = {
  action: Action;
  onDone: () => void;
  onToggleStar: () => void;
  onExit: () => void;
  onMail: () => void;
  onWhatsapp: () => void;
  onEditWhat: (text: string) => void;
};

function ActionRow({
  action,
  onDone,
  onToggleStar,
  onExit,
  onMail,
  onWhatsapp,
  onEditWhat,
}: ActionRowProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(action.what);

  const borderCls =
    action.urgency === "late_bad"
      ? "border-l-[3px] border-l-red-600"
      : action.urgency === "late_mid"
        ? "border-l-[3px] border-l-orange-400"
        : "";

  const commitEdit = () => {
    setEditing(false);
    onEditWhat(draft.trim());
  };

  return (
    <li
      className={`bg-white border border-stone rounded-lg px-4 py-3 grid grid-cols-[auto_1fr_auto] gap-3 items-start ${borderCls}`}
    >
      <button
        onClick={onToggleStar}
        title={action.is_estrella ? "Retirer Estrella" : "Mettre Estrella"}
        className={`mt-1 w-6 h-6 rounded-full border-[1.5px] flex items-center justify-center text-xs transition hover:scale-110 ${
          action.is_estrella
            ? "border-lemonade bg-lemonade text-charcoal"
            : "border-stone bg-white text-stone hover:border-charcoal hover:text-charcoal"
        }`}
        aria-label={action.is_estrella ? "Estrella" : "Non-Estrella"}
      >
        ★
      </button>
      <div className="flex flex-col gap-0.5 min-w-0">
        <div className="font-semibold text-sm flex items-baseline gap-2 flex-wrap">
          {action.lead_id ? (
            <Link
              to={`/crm/leads/${action.lead_id}`}
              className="text-charcoal hover:underline"
            >
              {action.full_name}
            </Link>
          ) : (
            <span className="text-charcoal">{action.full_name}</span>
          )}
          <span
            className="font-mono text-[10px] uppercase px-1.5 py-0.5 rounded text-white"
            style={{ backgroundColor: action.category_color }}
          >
            {action.category_name}
          </span>
        </div>

        {editing ? (
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitEdit();
              if (e.key === "Escape") {
                setDraft(action.what);
                setEditing(false);
              }
            }}
            autoFocus
            className="text-[13px] text-charcoal leading-snug px-2 py-1 border border-charcoal rounded bg-ivory focus:outline-none w-full"
          />
        ) : (
          <button
            onClick={() => {
              setDraft(action.what);
              setEditing(true);
            }}
            title="Cliquer pour modifier"
            className="text-[13px] text-concrete leading-snug text-left hover:bg-ivory rounded px-2 py-1 -mx-2 transition cursor-text"
          >
            {action.what}
          </button>
        )}

        <div className="flex gap-3 font-mono text-[10px] text-concrete uppercase mt-1">
          <span>
            Dernier : {action.last_contact_at?.slice(0, 10) ?? "—"}
          </span>
          {action.urgency === "late_bad" && (
            <span className="bg-red-600 text-white px-1.5 rounded font-bold">
              TRÈS EN RETARD · +{action.days_late}J
            </span>
          )}
          {action.urgency === "late_mid" && (
            <span className="bg-orange-100 text-orange-700 px-1.5 rounded font-bold">
              EN RETARD · +{action.days_late}J
            </span>
          )}
          {action.urgency === "due_today" && (
            <span className="bg-lemonade text-charcoal px-1.5 rounded font-bold">
              DUE TODAY
            </span>
          )}
          {action.primary_email && (
            <span className="text-ash truncate max-w-[180px]">
              {action.primary_email}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-1 items-stretch">
        <div className="flex gap-1">
          <ActionBtn
            label="✓"
            title="Marquer fait"
            onClick={onDone}
            variant="primary"
          />
          <ActionBtn
            label="✉"
            title={
              action.primary_email
                ? `Mail à ${action.primary_email}`
                : "Pas d'email — marque juste comme mail envoyé"
            }
            onClick={onMail}
            variant="secondary"
          />
          <ActionBtn
            label="💬"
            title={
              action.primary_phone
                ? `WhatsApp à ${action.primary_phone}`
                : "Pas de numéro — marque juste comme WA envoyé"
            }
            onClick={onWhatsapp}
            variant="secondary"
          />
        </div>
        <div className="flex gap-1">
          <Link
            to={`/crm/leads/${action.lead_id}`}
            className="flex-1 text-center px-2 py-1 rounded border border-stone bg-white text-charcoal hover:border-charcoal text-[10px] font-mono uppercase"
            title="Voir fiche complète"
          >
            Fiche
          </Link>
          <ActionBtn
            label="✕"
            title="Sortir du CRM (exit)"
            onClick={onExit}
            variant="danger"
          />
        </div>
      </div>
    </li>
  );
}

function ActionBtn({
  label,
  title,
  onClick,
  variant,
}: {
  label: string;
  title: string;
  onClick: () => void;
  variant: "primary" | "secondary" | "danger";
}) {
  const cls =
    variant === "primary"
      ? "bg-charcoal text-ivory border-charcoal hover:bg-black"
      : variant === "danger"
        ? "bg-white text-red-600 border-red-200 hover:bg-red-50 hover:border-red-600"
        : "bg-white text-charcoal border-stone hover:border-charcoal hover:bg-ivory";
  return (
    <button
      onClick={onClick}
      title={title}
      className={`flex-1 px-2 py-1 rounded border text-sm transition ${cls}`}
    >
      {label}
    </button>
  );
}

function ScoreCard({
  label,
  score,
  total,
  overdue,
  sublabel,
  histogram,
  accent = false,
}: {
  label: string;
  score: number;
  total: number;
  overdue: number;
  sublabel: string;
  histogram: { label: string; value: number }[];
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-lg p-6 border ${
        accent
          ? "bg-charcoal text-ivory border-charcoal"
          : "bg-white text-charcoal border-stone"
      }`}
    >
      <div className="flex items-baseline justify-between mb-2">
        <span
          className={`font-mono text-[11px] uppercase tracking-wider ${
            accent ? "text-lemonade" : "text-concrete"
          }`}
        >
          {label}
        </span>
        <span
          className={`font-mono text-[10px] uppercase ${
            accent ? "text-stone" : "text-ash"
          }`}
        >
          {total} leads
        </span>
      </div>
      <div className="flex items-baseline gap-2 mb-3">
        <span className="font-sans text-5xl font-black tracking-tight">
          {score}
          <span className="text-2xl">%</span>
        </span>
        {overdue > 0 && (
          <span
            className={`font-mono text-xs ${
              accent ? "text-lemonade" : "text-charcoal"
            }`}
          >
            · {overdue} {sublabel}
          </span>
        )}
      </div>
      <Histogram data={histogram} height={48} accentOnAccent={accent} />
    </div>
  );
}

function Histogram({
  data,
  height = 60,
  accentOnAccent = false,
}: {
  data: { label: string; value: number }[];
  height?: number;
  accentOnAccent?: boolean;
}) {
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <div className="flex items-end gap-1" style={{ height: `${height}px` }}>
      {data.map((d, i) => {
        const pct = (d.value / max) * 100;
        const bg = accentOnAccent
          ? "bg-lemonade"
          : pct < 50
            ? "bg-red-300"
            : pct < 80
              ? "bg-orange-300"
              : "bg-charcoal";
        return (
          <div
            key={i}
            className="flex-1 relative group"
            title={`${d.value} · ${d.label}`}
          >
            <div
              className={`${bg} rounded-t-sm`}
              style={{ height: `${Math.max(2, pct)}%` }}
            />
          </div>
        );
      })}
    </div>
  );
}
